import { useState, useEffect } from 'react'
import { useAdmin } from '../context/AdminContext'
import { createAdminApi, DbCachedResponseDetail, StoryListItem } from '../services/adminApi'
import styles from './CharactersPage.module.css'

interface Props {
  cachedResponseId: string
}

export default function CachedResponseDetailPage({ cachedResponseId }: Props) {
  const { apiKey } = useAdmin()

  const [item, setItem] = useState<DbCachedResponseDetail | null>(null)
  const [allStories, setAllStories] = useState<StoryListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  // Field editing
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState({ phrases: '', response: '', notFor: '', animations: '' })
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')

  // Story association
  const [storyError, setStoryError] = useState('')
  const [storyUpdating, setStoryUpdating] = useState<string | null>(null)

  // Per-story audio actions
  const [audioDeleting, setAudioDeleting] = useState<string | null>(null)
  const [audioPlaying, setAudioPlaying] = useState<string | null>(null)
  const [audioError, setAudioError] = useState('')

  // Audio regen + polling
  const [regenPolling, setRegenPolling] = useState(false)
  const [regenError, setRegenError] = useState('')
  const POLL_INTERVAL_MS = 3000
  const POLL_TIMEOUT_MS = 60000

  useEffect(() => {
    const api = createAdminApi(() => apiKey)
    setLoading(true)
    setError('')
    Promise.all([api.getDbCachedResponse(cachedResponseId), api.getStories()])
      .then(([cr, stories]) => {
        setItem(cr)
        setAllStories(stories)
      })
      .catch(e => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false))
  }, [apiKey, cachedResponseId])

  function startEdit() {
    if (!item) return
    setDraft({ phrases: item.phrases, response: item.response, notFor: item.notFor ?? '', animations: item.animations ?? '' })
    setSaveError('')
    setEditing(true)
  }

  async function saveFields() {
    if (!item) return
    const api = createAdminApi(() => apiKey)
    setSaving(true)
    setSaveError('')
    try {
      const updated = await api.updateDbCachedResponse(item.id, draft.phrases, draft.response, draft.notFor, draft.animations)
      setItem(updated)
      setEditing(false)
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : String(e))
    } finally {
      setSaving(false)
    }
  }

  async function toggleStory(storyId: string, checked: boolean) {
    if (!item) return
    const api = createAdminApi(() => apiKey)
    const currentIds = item.stories.map(s => s.id)
    const newIds = checked
      ? [...currentIds, storyId]
      : currentIds.filter(id => id !== storyId)
    setStoryError('')
    setStoryUpdating(storyId)
    try {
      const updated = await api.setDbCachedResponseStories(item.id, newIds)
      setItem(updated)
    } catch (e) {
      setStoryError(e instanceof Error ? e.message : String(e))
    } finally {
      setStoryUpdating(null)
    }
  }

  async function playStoryAudio(storyId: string, character: string, version: number) {
    if (!item) return
    setAudioError('')
    setAudioPlaying(storyId)
    try {
      const url = `/admin/cached-responses/${item.id}/audio?character=${encodeURIComponent(character)}&version=${version}`
      const res = await fetch(url, { headers: { 'X-API-Key': apiKey } })
      if (!res.ok) throw new Error(`${res.status} ${res.statusText}`)
      const blob = await res.blob()
      const objectUrl = URL.createObjectURL(blob)
      const audio = new Audio(objectUrl)
      audio.onended = () => { setAudioPlaying(null); URL.revokeObjectURL(objectUrl) }
      audio.onerror = () => { setAudioPlaying(null); URL.revokeObjectURL(objectUrl) }
      audio.play()
    } catch (e) {
      setAudioError(e instanceof Error ? e.message : String(e))
      setAudioPlaying(null)
    }
  }

  async function deleteStoryAudio(storyId: string, character: string, version: number) {
    if (!item) return
    const api = createAdminApi(() => apiKey)
    setAudioError('')
    setAudioDeleting(storyId)
    try {
      await api.deleteDbCachedResponseStoryAudio(item.id, character, version)
      // Refresh to update hasAudio flags
      const updated = await api.getDbCachedResponse(item.id)
      setItem(updated)
    } catch (e) {
      setAudioError(e instanceof Error ? e.message : String(e))
    } finally {
      setAudioDeleting(null)
    }
  }

  async function regenAudio() {
    if (!item) return
    const api = createAdminApi(() => apiKey)
    setRegenError('')
    try {
      await api.regenDbCachedResponseAudio(item.id)
      setRegenPolling(true)
    } catch (e) {
      setRegenError('Error: ' + (e instanceof Error ? e.message : String(e)))
    }
  }

  useEffect(() => {
    if (!regenPolling || !item) return
    const api = createAdminApi(() => apiKey)
    const audioCountAtStart = item.stories.filter(s => s.hasAudio).length
    let lastProgressCount = audioCountAtStart
    const deadline = Date.now() + POLL_TIMEOUT_MS

    const interval = setInterval(async () => {
      try {
        const updated = await api.getDbCachedResponse(item.id)
        const currentCount = updated.stories.filter(s => s.hasAudio).length
        const allDone = updated.stories.length === 0 || updated.stories.every(s => s.hasAudio)
        setItem(updated)

        if (allDone) {
          setRegenPolling(false)
          return
        }

        if (currentCount > lastProgressCount) {
          lastProgressCount = currentCount
        }

        if (Date.now() > deadline && currentCount === audioCountAtStart) {
          setRegenPolling(false)
          setRegenError('Audio regeneration may have failed — no audio was generated after 60 seconds.')
        }
      } catch {
        setRegenPolling(false)
      }
    }, POLL_INTERVAL_MS)

    return () => clearInterval(interval)
  }, [regenPolling, item?.id, apiKey]) // eslint-disable-line react-hooks/exhaustive-deps

  async function deleteItem() {
    if (!item || !confirm(`Delete cached response "${item.name}"? This cannot be undone.`)) return
    const api = createAdminApi(() => apiKey)
    try {
      await api.deleteDbCachedResponse(item.id)
      window.location.hash = '#cached-responses'
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    }
  }

  const linkedIds = new Set(item?.stories.map(s => s.id) ?? [])
  const dbStories = allStories.filter(s => s.inDatabase)

  return (
    <div className={styles.detailPage}>
      <header className={styles.detailHeader}>
        <a href="#cached-responses" className={styles.back}>← Cached Responses</a>
        {item && <h1>{item.name}</h1>}
      </header>

      <div className={styles.detailContent}>
        {loading && <p className={styles.empty}>Loading…</p>}
        {error && <p className={styles.error}>{error}</p>}

        {item && (
          <>
            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <p className={styles.sectionTitle}>Fields</p>
                {!editing && <button className={styles.editBtn} onClick={startEdit}>Edit</button>}
              </div>

              {editing ? (
                <>
                  <label className={styles.fieldLabel}>Phrases (slash-separated, supports wildcards)</label>
                  <input
                    className={styles.fieldInput}
                    value={draft.phrases}
                    onChange={e => setDraft(p => ({ ...p, phrases: e.target.value }))}
                    disabled={saving}
                    placeholder="hello / hey there / hi *"
                  />
                  <label className={styles.fieldLabel}>Response (exact text returned to the user)</label>
                  <textarea
                    className={styles.fieldInput}
                    value={draft.response}
                    onChange={e => setDraft(p => ({ ...p, response: e.target.value }))}
                    disabled={saving}
                    placeholder="Hello! How can I help you today?"
                    rows={4}
                  />
                  <label className={styles.fieldLabel}>Not For (phrases to suppress this response)</label>
                  <input
                    className={styles.fieldInput}
                    value={draft.notFor}
                    onChange={e => setDraft(p => ({ ...p, notFor: e.target.value }))}
                    disabled={saving}
                    placeholder="Optional exclusion phrases"
                  />
                  <label className={styles.fieldLabel}>Animations (comma-separated animation names)</label>
                  <input
                    className={styles.fieldInput}
                    value={draft.animations}
                    onChange={e => setDraft(p => ({ ...p, animations: e.target.value }))}
                    disabled={saving}
                    placeholder="Optional animation names"
                  />
                  {saveError && <p className={styles.saveError}>{saveError}</p>}
                  <div className={styles.editActions}>
                    <button className={styles.cancelBtn} onClick={() => setEditing(false)} disabled={saving}>
                      Cancel
                    </button>
                    <button className={styles.saveBtn} onClick={saveFields} disabled={saving}>
                      {saving ? 'Saving…' : 'Save'}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className={styles.fieldRow}>
                    <span className={styles.fieldLabel}>ID</span>
                    <span className={styles.fieldMono}>{item.id}</span>
                  </div>
                  <div className={styles.fieldRow}>
                    <span className={styles.fieldLabel}>Name</span>
                    <span className={styles.fieldValue}>{item.name}</span>
                  </div>
                  <div className={styles.fieldRow}>
                    <span className={styles.fieldLabel}>Phrases</span>
                    <span className={styles.fieldValue}>{item.phrases || '—'}</span>
                  </div>
                  <div className={styles.fieldRow}>
                    <span className={styles.fieldLabel}>Response</span>
                    <span className={styles.fieldValue}>{item.response || '—'}</span>
                  </div>
                  <div className={styles.fieldRow}>
                    <span className={styles.fieldLabel}>Not For</span>
                    <span className={styles.fieldValue}>{item.notFor || '—'}</span>
                  </div>
                  <div className={styles.fieldRow}>
                    <span className={styles.fieldLabel}>Animations</span>
                    <span className={styles.fieldValue}>{item.animations || '—'}</span>
                  </div>
                  <div className={styles.fieldRow}>
                    <span className={styles.fieldLabel}>Updated</span>
                    <span className={styles.fieldValue}>{new Date(item.updatedAt).toLocaleString()}</span>
                  </div>
                </>
              )}
            </div>

            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <p className={styles.sectionTitle}>Stories ({linkedIds.size} linked)</p>
              </div>

              {storyError && <p className={styles.saveError}>{storyError}</p>}
              {audioError && <p className={styles.saveError}>{audioError}</p>}

              {dbStories.length === 0 ? (
                <span className={styles.noStories}>No stories in database.</span>
              ) : (
                <table className={styles.storyTable}>
                  <thead>
                    <tr>
                      <th className={styles.storyTableCheck}></th>
                      <th className={styles.storyTableTitle}>Story</th>
                      <th className={styles.storyTableFile}>Filename</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dbStories.map(s => {
                      const linked = linkedIds.has(s.id)
                      const busy = storyUpdating === s.id
                      const linkedStory = item?.stories.find(ls => ls.id === s.id)
                      const hasAudio = linkedStory?.hasAudio ?? false
                      const character = linkedStory?.characterName ?? ''
                      const version = linkedStory?.characterVersion ?? 0
                      const api = createAdminApi(() => apiKey)
                      return (
                        <tr key={s.id} className={linked ? styles.storyRowLinked : styles.storyRow}>
                          <td className={styles.storyTableCheck}>
                            <input
                              type="checkbox"
                              checked={linked}
                              disabled={busy}
                              onChange={e => toggleStory(s.id, e.target.checked)}
                            />
                          </td>
                          <td className={styles.storyTableTitle}>{s.title || s.id}</td>
                          <td className={styles.storyTableFile}>{s.sourceFilename || '—'}</td>
                          <td>
                            {linked && character && (
                              <>
                                <button
                                  onClick={() => playStoryAudio(s.id, character, version)}
                                  disabled={!hasAudio || audioPlaying === s.id}
                                  style={{ marginRight: 8 }}
                                >
                                  {audioPlaying === s.id ? '▶ Playing…' : '▶ Play'}
                                </button>
                                <button
                                  onClick={() => deleteStoryAudio(s.id, character, version)}
                                  disabled={!hasAudio || audioDeleting === s.id}
                                >
                                  {audioDeleting === s.id ? 'Deleting…' : 'Delete Audio'}
                                </button>
                              </>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              )}
            </div>

            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <p className={styles.sectionTitle}>Audio</p>
              </div>
              <button className={styles.editBtn} onClick={regenAudio} disabled={regenPolling}>
                {regenPolling ? 'Regenerating…' : 'Regenerate Audio'}
              </button>
              {regenPolling && <span className={styles.fieldValue}> Checking for completion…</span>}
              {regenError && <p className={styles.saveError}>{regenError}</p>}
            </div>

            <div className={styles.section}>
              <p className={styles.sectionTitle}>Danger Zone</p>
              <button className={styles.deleteBtn} onClick={deleteItem}>Delete Cached Response</button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
